import { useCallback, useEffect, useState } from 'react';
import { ApiClient } from '@vanilla/services/ApiClient.js';

export function useNotifications() {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [listLoaded, setListLoaded] = useState(false);

    const fetchList = useCallback(async (p = 1, perPage = 20, filters = {}) => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: p, per_page: perPage, ...filters };
            // Use raw fetch to get envelope with meta (ApiClient.get unwraps data)
            const token = ApiClient.getToken();
            const base = ApiClient.getBaseUrl().replace(/\/+$/, '');
            const qs = ApiClient.buildQueryString(ApiClient.normalizeListParams(params));
            const res = await fetch(`${base}/api/v1/notifications${qs}`, {
                headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.error?.message || `HTTP ${res.status}`);
            const list = Array.isArray(json.data) ? json.data : [];
            const total = json.meta?.total ?? list.length;
            if (p === 1) setNotifications(list);
            else setNotifications((prev) => [...prev, ...list]);
            setHasMore(list.length === perPage && (p * perPage) < total);
            setPage(p);
            setListLoaded(true);
        } catch (e) {
            setError(e.message || 'Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }, []);

    const ensureListLoaded = useCallback(async () => {
        if (listLoaded) return;
        await fetchList(1, 20, {});
    }, [listLoaded, fetchList]);

    const fetchUnreadCount = useCallback(async () => {
        try {
            const token = ApiClient.getToken();
            const base = ApiClient.getBaseUrl().replace(/\/+$/, '');
            const res = await fetch(`${base}/api/v1/notifications/unread-count`, {
                headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            });
            const json = await res.json().catch(() => ({}));
            const count = json?.data?.count ?? json?.count ?? 0;
            setUnreadCount(Number(count) || 0);
        } catch {
            // silent
        }
    }, []);

    const markAsRead = useCallback(async (id) => {
        try {
            await ApiClient.patch(`/api/v1/notifications/${id}/read`, {});
            setNotifications((prev) => prev.map((n) => (String(n.id) === String(id) ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)));
            setUnreadCount((c) => Math.max(0, c - 1));
        } catch (e) {
            console.warn('markAsRead failed', e);
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        try {
            await ApiClient.post('/api/v1/notifications/read-all', {});
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (e) {
            console.warn('markAllAsRead failed', e);
        }
    }, []);

    const deleteNotification = useCallback(async (id) => {
        try {
            await ApiClient.delete(`/api/v1/notifications/${id}`);
            setNotifications((prev) => prev.filter((n) => String(n.id) !== String(id)));
        } catch (e) {
            console.warn('deleteNotification failed', e);
        }
    }, []);

    useEffect(() => {
        // Mount: unread badge only — full list loads when drawer opens
        fetchUnreadCount();
        const onVis = () => { if (document.visibilityState === 'visible') fetchUnreadCount(); };
        const onFocus = () => { fetchUnreadCount(); };
        const iv = setInterval(() => { fetchUnreadCount(); }, 30000);
        document.addEventListener('visibilitychange', onVis);
        window.addEventListener('focus', onFocus);
        return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onFocus); };
    }, [fetchUnreadCount]);

    useEffect(() => {
        const onNotification = (e) => {
            const detail = e.detail || {};
            const data = detail.data || detail.payload || detail;
            if (data?.id) {
                // Instant update without waiting for fetch
                setNotifications((prev) => {
                    if (prev.some((n) => String(n.id) === String(data.id))) return prev;
                    const mapped = {
                        id: data.id,
                        notification_type: data.type || data.notification_type || 'system_alert',
                        title: data.title || 'New notification',
                        body: data.body || null,
                        entity_type: data.entity_type || null,
                        entity_id: data.entity_id || null,
                        is_read: false,
                        created_at: data.created_at || new Date().toISOString(),
                        meta_data: data.metadata || data.meta_data || null,
                    };
                    return [mapped, ...prev].slice(0, 20);
                });
                setUnreadCount((c) => c + 1);
                fetchUnreadCount();
            }
        };
        const onRealtime = (e) => {
            const msg = e.detail || {};
            const isNotif = msg.type === 'notification' || msg.entity === 'notification';
            if (isNotif) {
                const payload = msg.data || msg.payload || msg;
                if (payload?.id) {
                    onNotification({ detail: payload });
                } else {
                    fetchUnreadCount();
                    if (listLoaded) fetchList(1, 20, {});
                }
            }
        };
        window.addEventListener('notification:received', onNotification);
        window.addEventListener('realtime:update', onRealtime);
        return () => {
            window.removeEventListener('notification:received', onNotification);
            window.removeEventListener('realtime:update', onRealtime);
        };
    }, [fetchUnreadCount, fetchList, listLoaded]);

    return {
        notifications,
        unreadCount,
        loading,
        error,
        page,
        hasMore,
        listLoaded,
        fetchList,
        ensureListLoaded,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        setNotifications,
    };
}
